import React from 'react';
import styled from 'styled-components/macro';
// types
import { QuoteItem } from 'queries/approveOrder/types';
import { Button } from 'react-covideo-common';
import { Backdrop } from './style';
import { useTranslation } from 'react-i18next';

type Props = {
  handleClose: () => void;
  handleSubmit: () => void;
  quoteItems: Array<QuoteItem>;
};

const Body = styled.div`
  padding: 32px;
  background-color: #ffffff;
  box-shadow:
    0px 0px 4px rgba(66, 79, 104, 0.08),
    0px 12px 20px rgba(66, 79, 104, 0.06);
  border-radius: 6px;
  width: 592px;

  @media screen and (max-width: 640px) {
    width: 100%;
    margin: 0 20px;
    padding: 20px 16px;
  }
`;

const Title = styled.div`
  font-weight: 700;
  font-size: 18px;

  @media screen and (max-width: 640px) {
    font-size: 16px;
  }
`;

const Row = styled.div`
  margin: 7px 0;

  @media screen and (max-width: 640px) {
    margin: 0;
    margin-bottom: 2px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  @media screen and (max-width: 640px) {
    margin-bottom: 25px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: end;
  margin-top: 32px;
  gap: 12px;

  @media screen and (max-width: 640px) {
    margin-top: 25px;
    flex-direction: column;
    justify-content: center;
  }
`;

const CloseButton = styled.div`
  cursor: pointer;
  color: #9297a2;
  font-weight: 600;
`;

const TaxTitle = styled.div`
  color: ${props => props.theme.colors.neutral[60]};
  font-weight: 400;
  font-size: 14px;
  margin-top: 32px;
`;

export const ConfirmSelectionModal = ({
  handleClose,
  quoteItems,
  handleSubmit,
}: Props) => {
  const { t } = useTranslation();
  const [approvedItems, setApprovedItems] = React.useState(
    [] as Array<QuoteItem>
  );
  const [deniedItems, setDeniedItems] = React.useState([] as Array<QuoteItem>);
  const [requestInfoItems, setRequestInfoItems] = React.useState(
    [] as Array<QuoteItem>
  );

  React.useEffect(() => {
    if (quoteItems.length) {
      const deniedQuoteItems = [] as Array<QuoteItem>;
      const approvedQuoteItems = quoteItems.filter((item: QuoteItem) => {
        if (item.hasRequestedInfo) {
          requestInfoItems.push(item);
          return false;
        }
        if (item.approved) return true;
        deniedQuoteItems.push(item);
        return false;
      });
      setApprovedItems(approvedQuoteItems);
      setDeniedItems(deniedQuoteItems);
      setRequestInfoItems(requestInfoItems);
    }
  }, []);

  const count = approvedItems.length;
  const amount = count
    ? approvedItems.reduce((a, b) => a + Number(b.price), 0).toFixed(2)
    : '0.00';

  const deniedCount = deniedItems.length;
  const deniedAmount = deniedCount
    ? deniedItems.reduce((a, b) => a + Number(b.price), 0).toFixed(2)
    : '0.00';
  const requestInfoCount = requestInfoItems.length;
  return (
    <Backdrop onClick={handleClose}>
      <Body>
        <Header>
          <Title>{t('Confirm Your Selection')}</Title>
          <CloseButton onClick={handleClose}>&#x2715;</CloseButton>
        </Header>

        <Row>
          {t('approvedSummary', {
            count,
            amount,
          })}
        </Row>
        <Row>
          {t('deniedSummary', { count: deniedCount, amount: deniedAmount })}
        </Row>
        <Row>{t('requestInfoSummary', { count: requestInfoCount })}</Row>

        <TaxTitle>*{t('plus taxes and applicable fees')}</TaxTitle>

        <ButtonContainer>
          <Button
            variant='secondary'
            text={t('Go Back')}
            onClick={handleClose}
          />
          <Button onClick={handleSubmit} text={t('Yes, Confirm & Send')} />
        </ButtonContainer>
      </Body>
    </Backdrop>
  );
};
